import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import TourCard from '../components/Tours/TourCard';
import {
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  StarIcon,
  CameraIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { tourService } from '../services/tourService';
import { useTheme } from '../contexts/ThemeContext';
import PricingFilter from '../components/Common/PricingFilter';
import { applyFeaturedOrder } from '../utils/applyFeaturedOrder';
import { usePricing } from '../contexts/PricingContext';
import { filterTours } from '../utils/filterTours';
import { Tour } from '../types';

const SafariTours: React.FC = () => {
  const { actualTheme } = useTheme();
  const pricingContext = usePricing();
  const [allSafariTours, setAllSafariTours] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Filtered tours based on global context
  const safariTours = React.useMemo(() => {
    return filterTours(allSafariTours, pricingContext);
  }, [allSafariTours, pricingContext]);

  useEffect(() => {
    const loadSafariTours = async () => {
      setLoading(true);
      try {
        const tours = await tourService.getActiveTours();
        const orderedTours = applyFeaturedOrder(tours);
        setAllSafariTours(orderedTours);
      } catch (error) {
        console.error('❌ Error loading Safari tours:', error);
        setAllSafariTours([]);
      } finally {
        setLoading(false);
      }
    };

    loadSafariTours();
  }, []);

  // Re-sort when SWR background fetch brings fresh sortOrder from live backend
  useEffect(() => {
    const handleToursUpdated = async () => {
      try {
        const tours = await tourService.getActiveTours();
        setAllSafariTours(applyFeaturedOrder(tours));
      } catch { /* silent */ }
    };

    window.addEventListener('toursUpdated', handleToursUpdated);
    return () => window.removeEventListener('toursUpdated', handleToursUpdated);
  }, []);

  // Listen for storage changes from other tabs (admin reordering)
  useEffect(() => {
    let isHandling = false;
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key && (e.key.startsWith('tours_') || e.key === 'tours_cache') && !isHandling) {
        isHandling = true;
        try {
          setLoading(true);
          const tours = await tourService.getActiveTours();
          setAllSafariTours(applyFeaturedOrder(tours));
        } catch (error) {
          console.error('Error loading Safari tours on storage change:', error);
          setAllSafariTours([]);
        } finally {
          setLoading(false);
          setTimeout(() => { isHandling = false; }, 1000);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);


  // No mock data - using only real admin data

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Floating Header Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10 md:pt-12">
        <div className={`rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-lg ${actualTheme === 'dark'
          ? 'bg-gradient-to-br from-primary-900 to-primary-800 border border-primary-700/50'
          : 'bg-gradient-to-br from-primary-800 to-primary-900 border border-primary-700/30'
          } text-white`}>
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Safari Tours
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-primary-100 max-w-2xl mx-auto leading-relaxed">
              Experience the thrill of African wildlife with our expertly guided safari tours.
              From the Great Migration to Big Five encounters, discover Africa's most iconic wildlife.
            </p>
          </div>
        </div>
      </div>

      {/* Global Pricing Filter */}
      <PricingFilter />

      {/* Features Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Our Safari Tours?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our safari experiences are designed to provide you with unforgettable wildlife encounters
              while ensuring your safety and comfort throughout your journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CameraIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Expert Photography</h3>
              <p className="text-neutral-600">Professional guides help you capture the perfect wildlife moments</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <HeartIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Conservation Focus</h3>
              <p className="text-neutral-600">Support wildlife conservation through responsible tourism</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <UsersIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Small Groups</h3>
              <p className="text-neutral-600">Intimate experiences with maximum 12 participants per tour</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <StarIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Premium Quality</h3>
              <p className="text-neutral-600">Luxury accommodations and world-class service</p>
            </div>
          </div>
        </div>
      </div>

      {/* Safari Tours Grid */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-primary-900 mb-4">Featured Safari Tours</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Choose from our carefully curated selection of safari experiences,
              each designed to showcase Africa's incredible wildlife.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-800"></div>
              <span className="ml-4 text-xl text-neutral-600">Loading safari tours...</span>
            </div>
          ) : safariTours.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {safariTours.map((tour) => (
                <TourCard
                  key={tour.id}
                  tour={tour}
                  showQuickBook={true}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-neutral-500 text-lg mb-4">
                No Safari tours available at the moment.
              </div>
              <p className="text-neutral-400 mb-6">
                Check back soon for new safari experiences, or explore our other tour categories.
              </p>
              <Link
                to="/tours"
                className="inline-flex items-center bg-primary-800 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                View All Tours
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Call to Action */}
      <div className="py-16 bg-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready for Your Safari Adventure?</h2>
          <p className="text-xl text-neutral-200 mb-8 max-w-3xl mx-auto">
            Book your safari today and create memories that will last a lifetime.
            Our expert guides are ready to show you Africa's incredible wildlife.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/tours"
              className="bg-white text-primary-800 hover:bg-neutral-100 px-8 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              View All Tours
            </Link>
            <Link
              to="/contact"
              className="bg-primary-700 hover:bg-primary-600 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafariTours;

